import { Controller } from "@hotwired/stimulus"

// Cycles the Bootstrap color theme between light, dark, and system,
// applying it to <html data-bs-theme> live and persisting the choice
// in cookies the server reads at first paint (see
// ApplicationController#resolved_theme). "system" follows
// prefers-color-scheme and tracks OS changes while selected.
const MODES = ["light", "dark", "system"]
const ONE_YEAR = 60 * 60 * 24 * 365

export default class extends Controller {
  static values = { mode: { type: String, default: "system" } }
  static targets = ["icon"]

  connect() {
    this.media = window.matchMedia("(prefers-color-scheme: dark)")
    this.boundSystemChange = () => {
      if (this.modeValue === "system") this.#applyResolved()
    }
    this.media.addEventListener("change", this.boundSystemChange)
    this.#apply()
  }

  disconnect() {
    this.media?.removeEventListener("change", this.boundSystemChange)
  }

  cycle() {
    const next = MODES[(MODES.indexOf(this.modeValue) + 1) % MODES.length]
    this.modeValue = next
    this.#setCookie("theme_mode", next)
    this.#apply()
  }

  modeValueChanged() {
    this.#renderIcon()
  }

  #apply() {
    if (this.modeValue === "system") {
      this.#applyResolved()
    } else {
      this.#setTheme(this.modeValue)
    }
    this.#renderIcon()
  }

  #applyResolved() {
    this.#setTheme(this.media.matches ? "dark" : "light")
  }

  #setTheme(theme) {
    document.documentElement.dataset.bsTheme = theme
    document.documentElement.dataset.themeMode = this.modeValue
    this.#setCookie("theme_resolved", theme)
  }

  // Replace the icon node rather than mutating its class. Font Awesome's
  // JS swaps the <i> for an <svg> whose className is a read-only
  // SVGAnimatedString and whose glyph is baked into a <path> (so
  // rewriting the class would neither assign nor visibly change it). A
  // fresh <i> is converted to the right glyph by FA's mutation
  // observer, which carries data-theme-target onto the new <svg>.
  #renderIcon() {
    if (!this.hasIconTarget) return
    const icon = { light: "fa-sun", dark: "fa-moon", system: "fa-circle-half-stroke" }[this.modeValue]
    const replacement = document.createElement("i")
    replacement.className = `fas ${icon}`
    replacement.dataset.themeTarget = "icon"
    this.iconTarget.replaceWith(replacement)
  }

  #setCookie(name, value) {
    document.cookie = `${name}=${value};path=/;max-age=${ONE_YEAR};samesite=lax`
  }
}
